import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Link as MuiLink,
  MenuItem,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SavingsIcon from '@mui/icons-material/Savings';

import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';
import { useAuth } from '@src/context';
import { toast } from '@src/components/core/toaster';
import {
  createMySetupIntent,
  detachMyPaymentMethod,
  fetchMyBillingLiveStatus,
  fetchMyPaymentMethods,
  listMyBillingProfiles,
  listMyThresholdCharges,
  registerMyPendingBankVerification,
  setMyDefaultPaymentMethod,
  updateMyPaymentPriority,
  updateMyThreshold,
} from '@src/api/myBilling';
import AddBankDialog from '@src/components/billing/AddBankDialog';
import RemovePaymentMethodDialog from '@src/components/billing/RemovePaymentMethodDialog';
import {
  cardExpirationStatus,
  formatCardExpiration,
  formatLastChargeLine,
  isUnverifiedBank as isUnverifiedBankFn,
  summarizePaymentHealth,
} from '@src/components/billing/paymentMethodStatus';

const niceStep = (max) => {
  if (max <= 1500) return 50;
  if (max <= 5000) return 100;
  if (max <= 10000) return 250;
  return 500;
};

const formatCurrency0dp = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const STATE_CHIP_COLORS = {
  healthy: 'success',
  warning: 'warning',
  paused: 'error',
  disabled: 'default',
};

const OUTCOME_CHIP_COLORS = {
  pending: 'default',
  charged: 'success',
  retried_success: 'success',
  failed: 'error',
  paused: 'error',
};

const PAYMENT_METHOD_LABELS = {
  card: 'Card only',
  card_then_ach: 'Card → ACH backup',
  ach_then_card: 'ACH → Card backup (lower fees)',
};

const PRIORITY_OPTIONS_CLIENT = [
  { value: 'card', label: 'Card only' },
  { value: 'card_then_ach', label: 'Card, fall back to ACH' },
  { value: 'ach_then_card', label: 'ACH, fall back to Card (lower fees)' },
];

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

// ── Add Card dialog (Stripe Elements) ─────────────────────────────────

function AddCardForm({ clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });
    if (result.error) {
      setError(result.error.message);
      setSaving(false);
      return;
    }
    if (result.setupIntent?.status === 'succeeded') {
      toast.success('Card saved.');
      onSuccess(result.setupIntent.payment_method);
    } else {
      setError('Setup did not complete. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Your card information is encrypted by Stripe before it leaves your
          browser. We never see your full card number.
        </DialogContentText>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#333' },
                invalid: { color: '#dc2626' },
              },
            }}
          />
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!stripe || saving}>
          {saving ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </form>
  );
}

function AddCardDialog({ open, onClose, profileId, isFirstCard, onAttached }) {
  const [intent, setIntent] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !profileId) return undefined;
    let cancelled = false;
    setIntent(null);
    setLoadError(null);
    createMySetupIntent(profileId, 'card')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data;
        if (!data?.client_secret || !data?.publishable_key) {
          setLoadError('Unable to start payment setup. Please try again later.');
          return;
        }
        setIntent(data);
        setStripePromise((prev) => prev || loadStripe(data.publishable_key));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail || err?.message || 'Failed to start setup.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a card</DialogTitle>
      {loadError ? (
        <>
          <DialogContent>
            <Alert severity="error">{loadError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : !intent || !stripePromise ? (
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Securing your connection to Stripe…
            </Typography>
          </Stack>
        </DialogContent>
      ) : (
        <Elements stripe={stripePromise}>
          <AddCardForm
            clientSecret={intent.client_secret}
            onSuccess={async (newPmId) => {
              if (isFirstCard && newPmId) {
                try {
                  await setMyDefaultPaymentMethod(profileId, newPmId);
                } catch {
                  /* non-fatal */
                }
              }
              onAttached();
              onClose();
            }}
            onCancel={onClose}
          />
        </Elements>
      )}
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────

export default function MyBillingPage() {
  const { state: authState } = useAuth();
  const isStaff =
    authState?.user?.is_staff || authState?.user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [sliderValue, setSliderValue] = useState(null);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [savingPriority, setSavingPriority] = useState(false);

  // Keep slider value in sync with the server's threshold when live status loads/reloads.
  useEffect(() => {
    if (liveStatus?.threshold_amount != null) {
      setSliderValue(Number(liveStatus.threshold_amount));
    }
  }, [liveStatus?.threshold_amount]);

  const locked = Boolean(liveStatus?.threshold_locked_by_staff);
  const bounds = liveStatus?.threshold_bounds;
  const sliderMin = bounds ? Math.floor(Number(bounds.min)) : 500;
  const sliderMax = bounds ? Math.ceil(Number(bounds.max)) : 10000;
  const sliderStep = niceStep(sliderMax);
  const sliderDirty =
    sliderValue != null &&
    Number(sliderValue) !== Number(liveStatus?.threshold_amount || 0);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === selectedProfileId) || null,
    [profiles, selectedProfileId],
  );

  const refreshPaymentMethods = useCallback(async () => {
    if (!selectedProfileId) return;
    setMethodsLoading(true);
    try {
      const resp = await fetchMyPaymentMethods(selectedProfileId);
      setPaymentMethods(resp?.data || null);
    } catch {
      setPaymentMethods(null);
    } finally {
      setMethodsLoading(false);
    }
  }, [selectedProfileId]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listMyBillingProfiles();
      const data = resp?.data;
      const rows = Array.isArray(data) ? data : data?.results || [];
      setProfiles(rows);
      if (rows.length && !selectedProfileId) {
        setSelectedProfileId(rows[0].id);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [selectedProfileId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Fetch live status + payment methods + ledger when the selected profile changes.
  useEffect(() => {
    if (!selectedProfileId) {
      setLiveStatus(null);
      setPaymentMethods(null);
      setLedgerEntries([]);
      return;
    }
    fetchMyBillingLiveStatus(selectedProfileId)
      .then((resp) => setLiveStatus(resp?.data || null))
      .catch(() => setLiveStatus(null));
    setMethodsLoading(true);
    fetchMyPaymentMethods(selectedProfileId)
      .then((resp) => setPaymentMethods(resp?.data || null))
      .catch(() => setPaymentMethods(null))
      .finally(() => setMethodsLoading(false));
    setLedgerLoading(true);
    listMyThresholdCharges({ client_id: selectedProfile?.client })
      .then((resp) => {
        const data = resp?.data;
        setLedgerEntries(Array.isArray(data) ? data : data?.results || []);
      })
      .catch(() => setLedgerEntries([]))
      .finally(() => setLedgerLoading(false));
  }, [selectedProfileId, selectedProfile?.client]);

  // Light polling for live spend.
  useEffect(() => {
    if (!selectedProfileId) return undefined;
    const interval = setInterval(() => {
      fetchMyBillingLiveStatus(selectedProfileId)
        .then((resp) => setLiveStatus(resp?.data || null))
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(interval);
  }, [selectedProfileId]);

  const progressPct = useMemo(() => {
    const v = liveStatus?.threshold_progress_pct;
    if (typeof v !== 'number') return 0;
    return Math.max(0, Math.min(100, v));
  }, [liveStatus]);

  const handleSaveThreshold = async () => {
    if (!selectedProfileId || sliderValue == null) return;
    setSavingThreshold(true);
    try {
      const resp = await updateMyThreshold(selectedProfileId, sliderValue);
      toast.success(`Threshold updated to ${formatCurrency(resp.data.threshold_amount)}.`);
      // Refresh live status so the new threshold + progress bar reflect.
      const statusResp = await fetchMyBillingLiveStatus(selectedProfileId);
      setLiveStatus(statusResp?.data || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || 'Could not update threshold.',
      );
    } finally {
      setSavingThreshold(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profiles.length) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Threshold billing isn't set up for your account yet. Reach out to your
          account manager if you'd like to enable it.
        </Alert>
      </Container>
    );
  }

  // Visibility gate: the self-serve billing experience is shown only to
  // clients explicitly opted into it (client_billing_ui_enabled). Everyone
  // else sees a neutral notice — their billing is unchanged and handled by
  // their account manager; this just hides the in-app UI during rollout.
  // Staff always see every client's billing UI regardless of the flag —
  // the gate is purely a CLIENT-facing rollout control.
  const billingUiEnabled = profiles.some(
    (p) => p.client_billing_ui_enabled,
  );
  if (!isStaff && !billingUiEnabled) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Your billing is managed directly by your Lead Gurus account
          manager. Please reach out to them with any billing questions.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Billing</Typography>
          <Typography variant="body2" color="text.secondary">
            Your current threshold billing settings and charge history
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={loadProfiles}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Profile selector (only if multiple accessible profiles) */}
      {profiles.length > 1 && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <TextField
              select
              fullWidth
              label="Client"
              value={selectedProfileId || ''}
              onChange={(e) => setSelectedProfileId(Number(e.target.value))}
            >
              {profiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.client_name}
                </MenuItem>
              ))}
            </TextField>
          </CardContent>
        </Card>
      )}

      {/* CARD 1 — Current billing status */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Current status" />
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Chip
              label={liveStatus?.state_display || 'Healthy'}
              color={STATE_CHIP_COLORS[liveStatus?.state || 'healthy']}
              sx={{ minWidth: 100 }}
            />
            <Box sx={{ flex: 1, width: '100%' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Unbilled ad spend
                </Typography>
                <Typography variant="caption">
                  {formatCurrency(liveStatus?.unbilled_spend)} /{' '}
                  {formatCurrency(liveStatus?.threshold_amount)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color={
                  progressPct >= 100 ? 'error' : progressPct >= 75 ? 'warning' : 'primary'
                }
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Stack>

          <Stack spacing={2}>
            {/* Threshold slider (or read-only when locked) */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Charge threshold
                  </Typography>
                  {locked && (
                    <Tooltip title="Your threshold is locked. Contact your account manager to adjust it.">
                      <LockIcon fontSize="inherit" color="action" />
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="h6">
                  {formatCurrency0dp(sliderValue ?? liveStatus?.threshold_amount)}
                </Typography>
              </Stack>
              {bounds && (
                <Box sx={{ px: 1.5 }}>
                  <Slider
                    value={Number(sliderValue ?? liveStatus?.threshold_amount ?? sliderMin)}
                    onChange={(_, value) => setSliderValue(value)}
                    disabled={locked || savingThreshold}
                    min={sliderMin}
                    max={sliderMax}
                    step={sliderStep}
                    valueLabelDisplay="off"
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mt: -0.5 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency0dp(sliderMin)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency0dp(sliderMax)}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {locked
                        ? 'Locked by your account manager.'
                        : 'Drag to adjust how often you get charged.'}
                    </Typography>
                    {sliderDirty && !locked && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveThreshold}
                        disabled={savingThreshold}
                      >
                        {savingThreshold ? 'Saving…' : 'Save threshold'}
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
            {(() => {
              const hasVerifiedBank = (paymentMethods?.methods || []).some(
                (m) => m.type === 'us_bank_account' && m.is_verified !== false,
              );
              const tierAchOk = Boolean(liveStatus?.tier_ach_eligible);
              const canChooseAch = hasVerifiedBank && tierAchOk;
              const cardOnly =
                (selectedProfile?.payment_method_priority || 'card') === 'card';
              const monthlySavings = Number(
                liveStatus?.fee_projection?.monthly_savings_on_ach || 0,
              );
              // $10/mo is the floor for showing the CTA — below that the
              // friction of switching exceeds the savings.
              const showSavingsCta =
                tierAchOk && cardOnly && monthlySavings >= 10;
              const switchToAch = async () => {
                setSavingPriority(true);
                try {
                  await updateMyPaymentPriority(
                    selectedProfileId,
                    'ach_then_card',
                  );
                  toast.success('Switched to ACH-first with card backup.');
                  await loadProfiles();
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail ||
                      err?.message ||
                      'Could not switch to ACH.',
                  );
                } finally {
                  setSavingPriority(false);
                }
              };
              return (
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Payment method
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={selectedProfile?.payment_method_priority || 'card'}
                      disabled={savingPriority}
                      onChange={async (e) => {
                        const next = e.target.value;
                        // ACH-primary is no longer gated behind prepaid —
                        // clients freely choose any priority (still tier-
                        // checked server-side).
                        setSavingPriority(true);
                        try {
                          await updateMyPaymentPriority(selectedProfileId, next);
                          toast.success('Payment method updated.');
                          await loadProfiles();
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail ||
                              err?.message ||
                              'Could not update payment method.',
                          );
                        } finally {
                          setSavingPriority(false);
                        }
                      }}
                      sx={{ minWidth: 240 }}
                    >
                      {PRIORITY_OPTIONS_CLIENT.map((opt) => {
                        const isAch = opt.value !== 'card';
                        const disabled = isAch && !canChooseAch;
                        return (
                          <MenuItem
                            key={opt.value}
                            value={opt.value}
                            disabled={disabled}
                          >
                            {opt.label}
                            {disabled
                              ? ` ${!tierAchOk ? '(not on your tier)' : '(connect a bank first)'}`
                              : ''}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Stack>
                  {showSavingsCta && (
                    <Alert
                      severity="success"
                      icon={<SavingsIcon />}
                      sx={{ mt: 1 }}
                      action={
                        hasVerifiedBank ? (
                          <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            onClick={switchToAch}
                            disabled={savingPriority}
                          >
                            Switch to ACH
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            startIcon={<AccountBalanceIcon />}
                            onClick={() => setAddBankOpen(true)}
                          >
                            Add bank
                          </Button>
                        )
                      }
                    >
                      <Typography variant="body2">
                        <strong>
                          Save ~{formatCurrency(monthlySavings)}/month
                        </strong>{' '}
                        in card processing fees by switching to ACH.
                        {hasVerifiedBank
                          ? ' Your bank is already connected — one click to switch.'
                          : ' Connect a US bank account to unlock lower fees (settles in 3–5 business days).'}
                      </Typography>
                    </Alert>
                  )}
                  {tierAchOk && !hasVerifiedBank && !showSavingsCta && (
                    <Typography variant="caption" color="text.secondary">
                      Connect a bank below to unlock lower-fee ACH options.
                    </Typography>
                  )}
                </Box>
              );
            })()}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Last charge
              </Typography>
              <Typography variant="body2">
                {formatDateTime(liveStatus?.last_threshold_charge_at)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Prepaid is dormant / staff-driven only — no client-facing enrollment
          CTA. (Removed per locked billing strategy: threshold-ACH is the
          model; prepaid is a reactive back-office tool, not a self-serve
          client offering.) */}

      {/* CARD 1.5 — Prepaid balance (only shown if an existing balance row
          exists — i.e. a client a staffer manually enrolled). General
          clients never have one, so this never renders for them. */}
      {liveStatus?.prepaid_balance && (() => {
        const pb = liveStatus.prepaid_balance;
        const stateChipColor = ({
          active: 'success',
          funding: 'info',
          draining: 'warning',
          depleted: 'default',
          frozen: 'error',
          inactive: 'default',
        })[pb.state] || 'default';
        const balanceNum = Number(pb.balance || 0);
        const targetNum = Number(pb.target_buffer || 0);
        const inFlightNum = Number(pb.in_flight_replenishment_amount || 0);
        const progress = Math.max(0, Math.min(100, Number(pb.balance_progress_pct || 0)));
        const runwayDays = pb.runway_days;
        return (
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title="Prepaid balance"
              subheader="Your funds on deposit. Ad spend draws from this instantly; we top it up via ACH automatically."
              action={<Chip size="small" label={pb.state_display || pb.state} color={stateChipColor} />}
            />
            <CardContent sx={{ pt: 0 }}>
              <Stack spacing={2}>
                {pb.state === 'frozen' && (
                  <Alert severity="error">
                    Your prepaid balance is currently frozen. Our team has been alerted and will reach out — your ads are paused until this is resolved.
                  </Alert>
                )}
                {pb.state === 'funding' && (
                  <Alert severity="info">
                    Your initial deposit is being processed via ACH. Once it clears (usually 3–5 business days), threshold billing will switch over automatically.
                  </Alert>
                )}
                {pb.state === 'draining' && (
                  <Alert severity="warning">
                    You've switched off ACH-prepaid. Existing balance is being drained on each charge; once it hits zero we'll route to your card.
                  </Alert>
                )}
                {pb.is_below_floor && pb.state === 'active' && inFlightNum === 0 && (
                  <Alert severity="info">
                    Your balance is below the auto-replenishment threshold. We'll initiate the next ACH replenishment shortly.
                  </Alert>
                )}
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="h5">
                      {formatCurrency(balanceNum)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      of {formatCurrency(targetNum)} target
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={2}
                >
                  {runwayDays != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Runway at current pace
                      </Typography>
                      <Typography variant="body2">
                        {runwayDays >= 1
                          ? `~${runwayDays.toFixed(1)} days`
                          : 'Less than a day'}
                      </Typography>
                    </Box>
                  )}
                  {inFlightNum > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Replenishment in flight
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(inFlightNum)} • arriving in 3–5 business days
                      </Typography>
                    </Box>
                  )}
                  {pb.last_replenished_at && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last replenished
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(pb.last_replenished_at)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })()}

      {/* CARD 2 — Payment methods */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Payment methods"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddCardIcon />}
                onClick={() => setAddCardOpen(true)}
              >
                Add card
              </Button>
              {liveStatus?.tier_ach_eligible && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => setAddBankOpen(true)}
                >
                  Add bank (ACH)
                </Button>
              )}
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {(() => {
            const priority = selectedProfile?.payment_method_priority || 'card';
            const order =
              priority === 'ach_then_card'
                ? ['Bank (ACH)', 'Card']
                : priority === 'card_then_ach'
                  ? ['Card', 'Bank (ACH)']
                  : ['Card', null];
            return (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                Charges go to <strong>{order[0]}</strong>
                {order[1] ? (
                  <>, falling back to <strong>{order[1]}</strong> if that fails.</>
                ) : (
                  ' only.'
                )}
              </Typography>
            );
          })()}
          {methodsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Stack>
          ) : paymentMethods?.note ? (
            <Alert severity="info">{paymentMethods.note}</Alert>
          ) : paymentMethods?.methods?.length ? (
            <Stack spacing={1.5}>
              {(() => {
                const health = summarizePaymentHealth(paymentMethods);
                const pendingBankWithUrl = health.pendingBanks.find(
                  (b) => b.verification_url,
                );
                // Bank-verification-in-progress gets its own richer Alert
                // when a bank is the only thing pending — clearer than
                // burying "your bank is being verified" inside a list.
                const bankOnlyIssue =
                  health.pendingBanks.length > 0 &&
                  health.expired.length === 0 &&
                  health.expiring.length === 0 &&
                  health.failingMethods.length === 0 &&
                  health.hasPrimary;
                if (bankOnlyIssue) {
                  return (
                    <Alert
                      severity="info"
                      sx={{ mb: 0.5 }}
                      action={
                        pendingBankWithUrl ? (
                          <Button
                            size="small"
                            color="inherit"
                            href={pendingBankWithUrl.verification_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Verify deposits →
                          </Button>
                        ) : null
                      }
                    >
                      <strong>Bank verification in progress.</strong> Stripe
                      sent two small test deposits — they typically arrive in
                      1–2 business days. Once they land, click below to enter
                      the amounts and finish setup.
                    </Alert>
                  );
                }
                if (!health.anyIssues) return null;
                const lines = [];
                if (health.pendingBanks.length) {
                  lines.push(
                    health.pendingBanks.length === 1
                      ? '1 bank is waiting on microdeposit verification.'
                      : `${health.pendingBanks.length} banks are waiting on microdeposit verification.`,
                  );
                }
                if (health.expired.length) {
                  lines.push(
                    health.expired.length === 1
                      ? '1 card has expired and can no longer be charged.'
                      : `${health.expired.length} cards have expired.`,
                  );
                }
                if (health.expiring.length) {
                  lines.push(
                    health.expiring.length === 1
                      ? '1 card expires within 60 days — please update it soon.'
                      : `${health.expiring.length} cards expire within 60 days.`,
                  );
                }
                if (health.failingMethods.length) {
                  lines.push(
                    'A recent charge was declined — please review your primary method.',
                  );
                }
                if (paymentMethods.methods.length > 0 && !health.hasPrimary) {
                  lines.push(
                    'No primary payment method set — click "Make primary" on the method you want charged first.',
                  );
                }
                const severity =
                  health.expired.length ||
                  health.failingMethods.length ||
                  (paymentMethods.methods.length > 0 && !health.hasPrimary)
                    ? 'error'
                    : 'warning';
                return (
                  <Alert
                    severity={severity}
                    sx={{ mb: 0.5 }}
                    action={
                      pendingBankWithUrl ? (
                        <Button
                          size="small"
                          color="inherit"
                          href={pendingBankWithUrl.verification_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Verify deposits →
                        </Button>
                      ) : null
                    }
                  >
                    {lines.length === 1 ? (
                      lines[0]
                    ) : (
                      <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                        {lines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </Stack>
                    )}
                  </Alert>
                );
              })()}
              {paymentMethods.methods.map((m) => {
                const isUnverifiedBank = isUnverifiedBankFn(m);
                const expStatus = cardExpirationStatus(m);
                const expLabel = formatCardExpiration(m);
                const lastChargeLine = formatLastChargeLine(m);
                const lastFailed =
                  m.last_charge && m.last_charge.succeeded === false;
                return (
                  <Stack
                    key={m.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack spacing={0.25}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2">{m.label}</Typography>
                        <Chip
                          size="small"
                          label={m.type === 'card' ? 'Card' : 'Bank'}
                          color={m.type === 'card' ? 'primary' : 'success'}
                          variant="outlined"
                        />
                        {m.is_default && (
                          <Chip size="small" label="Primary" color="success" />
                        )}
                        {m.type === 'us_bank_account' && !isUnverifiedBank && (
                          <Chip size="small" label="Verified" color="success" variant="outlined" />
                        )}
                        {isUnverifiedBank && (
                          <Chip
                            size="small"
                            label="Pending verification"
                            color="warning"
                          />
                        )}
                        {expStatus === 'expired' && (
                          <Chip size="small" label="Expired" color="error" />
                        )}
                        {expStatus === 'expiring' && (
                          <Chip
                            size="small"
                            label="Expires soon"
                            color="warning"
                          />
                        )}
                        {lastFailed && (
                          <Chip
                            size="small"
                            label="Last charge declined"
                            color="error"
                            variant="outlined"
                          />
                        )}
                        {isUnverifiedBank && m.verification_url && (
                          <MuiLink
                            href={m.verification_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                          >
                            Finish verifying →
                          </MuiLink>
                        )}
                      </Stack>
                      {(expLabel || lastChargeLine) && (
                        <Typography variant="caption" color="text.secondary">
                          {expLabel}
                          {expLabel && lastChargeLine ? ' • ' : ''}
                          {lastChargeLine}
                          {lastFailed && m.last_charge.error_message
                            ? ` — ${m.last_charge.error_message}`
                            : ''}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {!m.is_default && !isUnverifiedBank && expStatus !== 'expired' && (
                        <Button
                          size="small"
                          onClick={async () => {
                            try {
                              await setMyDefaultPaymentMethod(selectedProfileId, m.id);
                              toast.success('Primary card updated.');
                              refreshPaymentMethods();
                            } catch (err) {
                              toast.error(
                                err?.response?.data?.detail ||
                                  err?.message ||
                                  'Could not update primary.',
                              );
                            }
                          }}
                        >
                          Make primary
                        </Button>
                      )}
                      {!m.is_default && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setMethodToRemove(m)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No payment methods yet. Click <strong>Add card</strong> to get started.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* CARD 3 — Charge history */}
      <Card>
        <CardHeader
          title="Recent charges"
          action={
            <Button
              size="small"
              component={RouterLink}
              to={paths.myBillingActivity}
            >
              View all
            </Button>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {ledgerLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Stack>
          ) : ledgerEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No charges yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerEntries.slice(0, 25).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.triggered_at)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.cumulative_spend)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.outcome_display || row.outcome}
                          color={OUTCOME_CHIP_COLORS[row.outcome] || 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <AddCardDialog
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        profileId={selectedProfileId}
        isFirstCard={!paymentMethods?.methods?.length}
        onAttached={refreshPaymentMethods}
      />

      <AddBankDialog
        open={addBankOpen}
        onClose={() => setAddBankOpen(false)}
        profileId={selectedProfileId}
        createSetupIntent={createMySetupIntent}
        registerPendingVerification={registerMyPendingBankVerification}
        onAttached={async (newPmId) => {
          // Only auto-promote an instantly-verified bank to primary on a
          // first-time attach. Microdeposit-pending banks (newPmId=null
          // when pendingVerification=true) skip this branch entirely so
          // we don't route charges to a bank that can't be charged yet.
          if (newPmId && !paymentMethods?.methods?.length) {
            try {
              await setMyDefaultPaymentMethod(selectedProfileId, newPmId);
            } catch {
              /* non-fatal */
            }
          }
          refreshPaymentMethods();
        }}
        settlementCopy={
          'ACH charges settle in 3–5 business days, so the very first few ' +
          'charges may briefly overlap. Stripe fees are about 5× lower than card.'
        }
      />

      <RemovePaymentMethodDialog
        open={Boolean(methodToRemove)}
        method={methodToRemove}
        onClose={() => setMethodToRemove(null)}
        onConfirm={async (m) => {
          await detachMyPaymentMethod(selectedProfileId, m.id);
          toast.success('Payment method removed.');
          setMethodToRemove(null);
          refreshPaymentMethods();
        }}
      />

      {/* Prepaid enrollment dialogs removed from the client portal —
          prepaid is dormant / staff-driven only under the locked billing
          strategy. No client-facing prepaid surface. */}
    </Container>
  );
}
